<template>
    <div class="header-wrapper">
        <div className="left">
            <SectionNav />

            <Button data-testid="connect__add-meta-account-button" @click="$emit('update:show-add-account-modal', true)"
                >Add Meta Account</Button
            >
            <LabeledSwitch
                type="info"
                :model-value="connectStore.showDeletedAccounts"
                data-testid="connect__show-deleted-accounts"
                @update:modelValue="connectStore.showDeletedAccounts = $event"
            >
                Show deleted accounts and rules
            </LabeledSwitch>
        </div>

        <div v-if="connectStore.accounts?.length" class="right">
            <template v-if="connectStore.hasAccountChanges">
                <Button data-testid="connect__reset_changes" @click="cancelChangesModal = true">Reset</Button>
                <Button data-testid="connect__save_changes" @click="saveChangesModal = true">Save</Button>
            </template>
            <Button
                :disabled="!publishMetaAccountsStatus.canPublish"
                type="primary"
                data-testid="connect__publish-all"
                @click="publishChangesModal = true"
                >Publish all</Button
            >
        </div>

        <alert-modal
            v-model="cancelChangesModal"
            header="Reset changes"
            title="Are you sure?"
            content="Unsaved changes will be lost. This cannot be undone."
            ok-text="Reset"
            :on-ok="cancelChanges"
        />

        <alert-modal
            v-model="saveChangesModal"
            header="Save changes"
            title="Are you sure?"
            content="You will save the changes made during the current session."
            ok-text="Save"
            :warning="true"
            :on-ok="saveChanges"
        />

        <alert-modal
            v-model="publishChangesModal"
            header="Publish changes"
            title="Are you sure?"
            content="Your unsaved changes will be saved and published."
            ok-text="Publish"
            :warning="true"
            :on-ok="publishChanges"
        />

        <PlatformAccountFormModal
            v-if="showAddAccountModal"
            @close="$emit('update:show-add-account-modal', false)"
            @saveAccount="onAddAccount"
        />
    </div>
</template>

<script lang="ts">
import { mapStores } from "pinia";
import type { PropType } from "vue";

import AlertModal from "@/components/Modal/Alert.vue";
import LabeledSwitch from "@/components/Campaign/LabeledSwitch.vue";
import PlatformAccountFormModal from "@/Connect/components/Accounts/PlatformAccountEdit.vue";
import SectionNav from "@/components/SectionNav.vue";

import { useConnectStore } from "@/Connect/store";
import type { PlatformAccount } from "@/Connect/types";

export default {
    components: { SectionNav, AlertModal, LabeledSwitch, PlatformAccountFormModal },

    props: {
        showAddAccountModal: { type: Boolean },
        publishMetaAccountsStatus: {
            type: Object as PropType<{
                canPublish: boolean;
                message: string;
                extraInfo: {
                    label: string;
                    errors: {
                        editableGroup: string;
                        error: string;
                    }[];
                }[];
            }>,
            required: true
        }
    },

    emits: ["save-changes", "publish-changes", "update:show-add-account-modal"],

    data() {
        return {
            cancelChangesModal: false,
            saveChangesModal: false,
            publishChangesModal: false
        };
    },

    computed: {
        ...mapStores(useConnectStore)
    },

    methods: {
        onAddAccount(accountData: PlatformAccount) {
            this.connectStore.addPlatformAccount(accountData);
        },

        cancelChanges() {
            this.connectStore.resetToSavedAccounts();
        },

        saveChanges() {
            this.$emit("save-changes");
        },

        publishChanges() {
            this.$emit("publish-changes");
        }
    }
};
</script>

<style lang="css" scoped>
.header-wrapper {
    box-sizing: border-box;
    display: flex;
    justify-content: space-between;
    align-items: center;

    width: 100%;
    height: 60px;

    position: fixed;
    z-index: 1;

    background-color: #fff;
    border-bottom: 1px solid #c1c7cd;

    padding: 0 20px;
}

.left,
.right {
    display: flex;
    gap: 10px;
    align-items: center;
}
</style>
